import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Search, Plus, Trash2, Link, Upload, CheckSquare, X, LayoutGrid, LayoutList } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'
import YTChannelManager from './YTChannelManager'
import YTPlaylistManager from './YTPlaylistManager'
import YTVideoList from './YTVideoList'
import YTCardList from './YTCardList'
import YTVideoDetail from './YTVideoDetail'
// YTLinkModal removed — content creation now uses CatalogCreate via navigation
import YTImportModal from './YTImportModal'
import './YouTubeArchive.css'

export default function YouTubeArchive() {
  const { api } = useAuth()
  const navigate = useNavigate()

  const [channels, setChannels] = useState([])
  const [videos, setVideos] = useState([])
  const [allTags, setAllTags] = useState([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [status, setStatus] = useState('')

  const [selected, setSelected] = useState(null)
  const [showChannelMgr, setShowChannelMgr] = useState(false)
  const [showPlaylistMgr, setShowPlaylistMgr] = useState(false)  // dev24
  // showLinkModal removed — content creation uses CatalogCreate navigation
  const [showImport, setShowImport] = useState(false)

  // TBTV Channels + CF Live Inputs for pickers
  const [tbtvChannels, setTbtvChannels] = useState([])
  const [cfLiveInputs, setCfLiveInputs] = useState([])

  // Filters
  const [channelFilter, setChannelFilter] = useState('')
  const [eventFilter, setEventFilter] = useState('')
  const [linkedFilter, setLinkedFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // Multi-select
  const [checked, setChecked] = useState(new Set())

  // View mode
  const [viewMode, setViewMode] = useState('table') // 'table' or 'cards'

  const loadChannels = useCallback(async () => {
    try {
      const data = await api('/youtube/channels')
      setChannels(data.channels || [])
    } catch {}
  }, [api])

  const loadTbtvChannels = useCallback(async () => {
    try { setCfLiveInputs((await api('/live-inputs')).liveInputs || []) } catch {}
    try { setTbtvChannels((await api('/channels')).channels || []) } catch {}
  }, [api])

  const loadVideos = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (channelFilter) params.set('channel', channelFilter)
      if (eventFilter) params.set('event_type', eventFilter)
      if (linkedFilter) params.set('linked', linkedFilter)
      if (searchTerm) params.set('search', searchTerm)
      const data = await api(`/youtube/videos?${params}`)
      setVideos(data.videos || [])
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setLoading(false)
  }, [api, channelFilter, eventFilter, linkedFilter, searchTerm])

  const loadTags = useCallback(async () => {
    try {
      const data = await api('/tags')
      setAllTags(data.tags || [])
    } catch {}
  }, [api])

  useEffect(() => { loadChannels(); loadTags(); loadTbtvChannels() }, [loadChannels, loadTags, loadTbtvChannels])
  useEffect(() => { loadVideos() }, [loadVideos])

  // Sync
  async function handleSync() {
    setSyncing(true)
    setStatus('Syncing all channels...')
    try {
      const data = await api('/youtube/sync', { method: 'POST' })
      const summary = (data.results || []).map(r =>
        r.error ? `${r.channel}: ❌ ${r.error}` : `${r.channel}: +${r.added} new, ${r.updated} updated`
      ).join(' | ')
      setStatus(`✅ ${summary}`)
      loadVideos()
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSyncing(false)
  }

  async function handleSyncChannel(channelId) {
    setSyncing(true)
    try {
      const data = await api(`/youtube/sync/${channelId}`, { method: 'POST' })
      setStatus(`✅ ${data.channel}: +${data.added} new, ${data.updated} updated`)
      loadVideos()
      loadChannels()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSyncing(false)
  }

  // Single delete
  async function handleDelete(ytId) {
    if (!confirm('Delete this YouTube video from archive?')) return
    try {
      await api(`/youtube/videos/${ytId}`, { method: 'DELETE' })
      setStatus('✅ Deleted')
      if (selected?.youtube_id === ytId) setSelected(null)
      loadVideos()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function handleUpdate() {
    loadVideos()
    loadTags()
  }

  // ---- Multi-select ----
  function handleToggle(ytId) {
    setChecked(prev => {
      const next = new Set(prev)
      next.has(ytId) ? next.delete(ytId) : next.add(ytId)
      return next
    })
  }

  function handleToggleAll() {
    if (checked.size === videos.length && videos.every(v => checked.has(v.youtube_id))) {
      setChecked(new Set())
    } else {
      setChecked(new Set(videos.map(v => v.youtube_id)))
    }
  }

  function clearSelection() {
    setChecked(new Set())
  }

  // Bulk delete
  async function handleBulkDelete() {
    const ids = [...checked]
    if (!confirm(`Delete ${ids.length} videos from YouTube Archive?\n\nThis cannot be undone.`)) return
    setStatus(`Deleting ${ids.length} videos...`)
    let success = 0, errors = 0
    for (const ytId of ids) {
      try {
        await api(`/youtube/videos/${ytId}`, { method: 'DELETE' })
        success++
      } catch { errors++ }
    }
    setStatus(`✅ ${success} deleted${errors ? `, ${errors} failed` : ''}`)
    clearSelection()
    if (selected && checked.has(selected.youtube_id)) setSelected(null)
    loadVideos()
  }

  // Bulk status change
  async function handleBulkStatus(newStatus) {
    const ids = [...checked]
    setStatus(`Updating ${ids.length} videos to ${newStatus}...`)
    let success = 0, errors = 0
    for (const ytId of ids) {
      try {
        await api(`/youtube/videos/${ytId}`, {
          method: 'PATCH',
          body: JSON.stringify({ event_type: newStatus })
        })
        success++
      } catch { errors++ }
    }
    setStatus(`✅ ${success} updated to ${newStatus}${errors ? `, ${errors} failed` : ''}`)
    clearSelection()
    loadVideos()
  }

  // Navigate to Content Catalog to create entry for first unlinked video
  function handleBulkCreateContent() {
    const unlinked = videos.filter(v => checked.has(v.youtube_id) && !v.tbtv_id)
    if (unlinked.length === 0) {
      setStatus('All selected videos are already in the Content Library.')
      return
    }
    navigate(`/ondemand/catalog?create=true&youtube_id=${unlinked[0].youtube_id}`)
  }

  async function handleBulkTbtvChannel(tbtvChannelId) {
    const ch = tbtvChannels.find(c => c.tbtv_channel_id === tbtvChannelId)
    if (!ch) return
    const ids = [...checked]
    setStatus(`Assigning ${ids.length} videos to ${ch.name}...`)
    let ok = 0, fail = 0
    for (const ytId of ids) {
      try {
        await api(`/youtube/videos/${ytId}`, { method: 'PATCH', body: JSON.stringify({ viloud_channel_name: ch.name || '', viloud_channel_id: ch.viloud_id || '' }) })
        ok++
      } catch { fail++ }
    }
    setStatus(`✅ ${ok} assigned to ${ch.name}${fail ? `, ${fail} failed` : ''}`)
    clearSelection(); loadVideos()
  }

  async function handleBulkCfInput(uid) {
    const li = cfLiveInputs.find(i => i.uid === uid)
    const ids = [...checked]
    setStatus(`Assigning CF input to ${ids.length} videos...`)
    let ok = 0, fail = 0
    for (const ytId of ids) {
      try {
        await api(`/youtube/videos/${ytId}`, { method: 'PATCH', body: JSON.stringify({ cf_live_input_id: uid }) })
        ok++
      } catch { fail++ }
    }
    setStatus(`✅ ${ok} assigned CF input${fail ? `, ${fail} failed` : ''}`)
    clearSelection(); loadVideos()
  }

  return (
    <div className="yt-archive">
      <div className="yt-header">
        <div className="yt-header-left">
          <h1>YouTube Archive</h1>
          <span className="yt-count">{videos.length} videos</span>
        </div>
        <div className="yt-header-actions">
          <div className="yt-view-toggle">
            <button
              className={`yt-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <LayoutGrid size={16} />
            </button>
            <button
              className={`yt-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table view"
            >
              <LayoutList size={16} />
            </button>
          </div>
          <button className="btn-ghost" onClick={() => setShowImport(true)}>
            <Upload size={14} /> Import CSV
          </button>
          <button className="btn-ghost" onClick={() => setShowChannelMgr(true)}>
            <Plus size={14} /> Channels ({channels.length})
          </button>
          {/* dev24: playlist manager — sibling to Channels, opens a modal. Uses
              the existing yt-channel-modal CSS so styling matches. */}
          <button className="btn-ghost" onClick={() => setShowPlaylistMgr(true)}>
            <Plus size={14} /> Playlists
          </button>
          <button className="btn-primary" onClick={handleSync} disabled={syncing}>
            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Sync All'}
          </button>
        </div>
      </div>

      {status && (
        <div className={`yt-status ${status.startsWith('Error') ? 'error' : ''}`}>
          {status}
          <button className="btn-ghost btn-sm" onClick={() => setStatus('')}>✕</button>
        </div>
      )}

      {/* Bulk Action Bar */}
      {checked.size > 0 && (
        <div className="yt-bulk-bar">
          <span className="yt-bulk-count"><CheckSquare size={14} /> {checked.size} selected</span>
          <select
            className="yt-bulk-status"
            defaultValue=""
            onChange={e => { if (e.target.value) { handleBulkStatus(e.target.value); e.target.value = '' } }}
          >
            <option value="" disabled>Change Status…</option>
            <option value="upcoming">→ Upcoming</option>
            <option value="live">→ Live</option>
            <option value="completed">→ Completed</option>
          </select>
          <select className="yt-bulk-status" defaultValue="" onChange={e => { if (e.target.value) { handleBulkTbtvChannel(e.target.value); e.target.value = '' } }}>
            <option value="" disabled>Assign Channel…</option>
            {tbtvChannels.map(tc => <option key={tc.viloud_id} value={tc.tbtv_channel_id}>{tc.name} [{tc.channel_type || 'linear'}]</option>)}
          </select>
          <select className="yt-bulk-status" defaultValue="" onChange={e => { if (e.target.value) { handleBulkCfInput(e.target.value); e.target.value = '' } }}>
            <option value="" disabled>Assign CF Input…</option>
            {cfLiveInputs.map(li => <option key={li.uid} value={li.uid}>{li.meta?.name || 'Unnamed'} ({li.uid.substring(0, 8)}…)</option>)}
          </select>
          <button onClick={handleBulkCreateContent}>
            <Link size={12} /> Create Content Entry
          </button>
          <button onClick={handleBulkDelete} className="danger">
            <Trash2 size={12} /> Delete
          </button>
          <button onClick={clearSelection} className="yt-bulk-clear">
            <X size={12} /> Clear
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="yt-filters">
        <div className="filter-group">
          <select value={channelFilter} onChange={e => setChannelFilter(e.target.value)}>
            <option value="">All Channels</option>
            {channels.map(ch => (
              <option key={ch.youtube_channel_id} value={ch.youtube_channel_id}>{ch.name}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <select value={eventFilter} onChange={e => setEventFilter(e.target.value)}>
            <option value="">All Types</option>
            <option value="upcoming">Upcoming</option>
            <option value="live">Live</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        <div className="filter-group">
          <select value={linkedFilter} onChange={e => setLinkedFilter(e.target.value)}>
            <option value="">All</option>
            <option value="false">Unlinked</option>
            <option value="true">Linked to VOD</option>
          </select>
        </div>
        <div className="filter-group search-group">
          <Search size={14} />
          <input
            type="text"
            placeholder="Search videos..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {viewMode === 'table' ? (
        <>
          {/* Table mode: Detail Panel TOP, Video Table BOTTOM */}
          <div className="yt-detail-panel">
            {selected ? (
              <YTVideoDetail
                video={selected}
                channels={channels}
                allTags={allTags}
                tbtvChannels={tbtvChannels}
                cfLiveInputs={cfLiveInputs}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onLink={() => navigate(`/ondemand/catalog?create=true&youtube_id=${selected.youtube_id}`)}
                onReselect={v => setSelected(v)}
              />
            ) : (
              <div className="yt-detail-empty">
                <p className="muted">Select a video from the table below to view details and preview</p>
              </div>
            )}
          </div>

          <div className="yt-table-panel">
            <YTVideoList
              videos={videos}
              loading={loading}
              selected={selected}
              onSelect={setSelected}
              channels={channels}
              checked={checked}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
            />
          </div>
        </>
      ) : (
        <>
          {/* Cards mode: Sidebar LEFT, Detail RIGHT */}
          <div className="yt-cards-layout">
            <div className="yt-cards-sidebar">
              <YTCardList
                videos={videos}
                loading={loading}
                selected={selected}
                onSelect={setSelected}
                channels={channels}
                checked={checked}
                onToggle={handleToggle}
                onToggleAll={handleToggleAll}
              />
            </div>

            <div className="yt-cards-main">
              {selected ? (
                <YTVideoDetail
                  video={selected}
                  channels={channels}
                  allTags={allTags}
                  tbtvChannels={tbtvChannels}
                  cfLiveInputs={cfLiveInputs}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  onLink={() => navigate(`/ondemand/catalog?create=true&youtube_id=${selected.youtube_id}`)}
                  onReselect={v => setSelected(v)}
                  layout="single"
                />
              ) : (
                <div className="yt-detail-empty">
                  <p className="muted">Select a video to view details and preview</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {showChannelMgr && (
        <YTChannelManager
          channels={channels}
          onClose={() => setShowChannelMgr(false)}
          onUpdate={() => { loadChannels(); loadVideos() }}
          onSync={handleSyncChannel}
          syncing={syncing}
        />
      )}

      {showImport && (
        <YTImportModal
          onClose={() => setShowImport(false)}
          onComplete={() => { setShowImport(false); loadVideos(); setStatus('✅ CSV imported') }}
        />
      )}

      {/* dev24: playlist manager modal */}
      {showPlaylistMgr && (
        <YTPlaylistManager
          onClose={() => setShowPlaylistMgr(false)}
          onUpdate={() => { loadVideos() }}
        />
      )}
    </div>
  )
}
